import { Dialog } from '@angular/cdk/dialog';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, take } from 'rxjs';
import { filter } from 'rxjs/operators';
import { JoinCompanyService } from '../../services/join-company.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { selectCompanyData } from '../../stores/company/company.selector';
import * as ProfileActions from '../../stores/profile/profile.actions';
import { selectProfileData } from '../../stores/profile/profile.selector';
import { LoadingState, ProfileState } from '../../stores/store.states';
import {
  dispatchLoadingProfileTimeLocked,
  dispatchLoadingProfileWithCompanyAndEquipment,
} from '../../stores/store.utils';
import { InformationModalComponent } from '../modals/information-modal/information-modal.component';

export interface OptionsCountry {
  code: string;
  title: string;
}

@Component({
  selector: 'app-profile-form',
  templateUrl: './profile-form.component.html',
  styleUrls: ['./profile-form.component.scss'],
  standalone: false,
})
export class ProfileFormComponent {
  protected readonly LoadingState = LoadingState;
  successfulEdit: boolean = false;
  showResponseBanner: boolean = false;
  avatar?: string;

  form: FormGroup = this.formBuilder.nonNullable.group({
    firstName: this.formBuilder.control('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    lastName: this.formBuilder.control('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    jobTitle: this.formBuilder.control('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    email: this.formBuilder.control(
      { value: '', disabled: true },
      {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.email,
          Validators.maxLength(100),
        ],
      },
    ),
  });

  profile$: Observable<ProfileState>;

  constructor(
    private formBuilder: FormBuilder,
    private store: Store,
    private localStorageSerivce: LocalStorageService,
    private joingCompanyService: JoinCompanyService,
    private router: Router,
    private dialog: Dialog,
  ) {
    dispatchLoadingProfileTimeLocked(store);
    this.profile$ = this.store.select(selectProfileData);
    this.profile$.subscribe((profile: ProfileState) => {
      if (profile.avatar) this.avatar = profile.avatar;
      if (profile.firstName) {
        this.form.patchValue({
          firstName: profile.firstName,
          lastName: profile.lastName,
          jobTitle: profile.jobTitle,
          email: profile.email,
        });
      }
    });
  }

  getAvatar(image: string) {
    this.avatar = image;
  }

  update() {
    this.store.dispatch(
      ProfileActions.editingQuantumProfile({
        quantumProfile: {
          ...this.form.value,
          avatar: this.avatar,
        },
      }),
    );
    this.profile$
      .pipe(
        filter(
          (profile) =>
            profile.updatedState === LoadingState.SUCCESS ||
            profile.updatedState === LoadingState.ERROR,
        ),
        take(1),
      )
      .subscribe((profile: ProfileState) => {
        if (profile.updatedState === LoadingState.SUCCESS) {
          this.successfulEdit = true;
          this.showResponseBanner = true;
          const invite =
            this.localStorageSerivce.getInviteLinkFromLocalStorage();
          if (invite) {
            this.joingCompanyService.joinCompany(invite.joinLink).subscribe({
              next: (joinResult) => {
                this.localStorageSerivce.removeInviteLinkFromLocalStorage();
                this.openInformationDialogToConfirmCompletion();
              },
              error: (error) => {},
            });
          }
        } else if (profile.updatedState === LoadingState.ERROR) {
          this.successfulEdit = false;
          this.showResponseBanner = true;
        }
      });
  }

  openInformationDialogToConfirmCompletion() {
    dispatchLoadingProfileWithCompanyAndEquipment(this.store);
    this.store
      .select(selectCompanyData)
      .pipe(
        filter((company) => company.loadingState === LoadingState.SUCCESS),
        take(1),
      )
      .subscribe((company) => {
        const dialogRef = this.dialog.open(InformationModalComponent, {
          data: {
            headerText: 'Congratulations on joining ' + company.name,
            bodyText: 'You are now in the company group ' + company.name + '!',
          },
        });
        dialogRef.closed.subscribe((_) => {
          this.router.navigate(['/profile/company']);
        });
      });
  }

  getFormErrors(): string[] {
    const errorMessages: string[] = [];

    for (const controlName in this.form.controls) {
      const control = this.form.get(controlName);
      if (control && control.invalid && (control.dirty || control.touched)) {
        if (control.errors?.['required']) {
          errorMessages.push(`${controlName} is required.`);
        }
        if (control.errors?.['email']) {
          errorMessages.push('Please enter a valid email address.');
        }
        if (control.errors?.['maxLength']) {
          errorMessages.push(`${controlName} is too long.`);
        }
      }
    }

    return errorMessages;
  }
}
